import type { ListingDesignItem, ListingMeta } from '../domain/models/ProductListing';
import { plpLscacheGet, plpLscacheRemove, plpLscacheSet } from './plpLscache';
import { readPlpRestore, type PlpRestoreState } from './plpScrollRestore';

export const PLP_SNAPSHOT_KEY_PREFIX = 'plp_snapshot_';

export interface PlpSessionSnapshot {
  version: 1;
  listingMeta: ListingMeta;
  designs: ListingDesignItem[];
  currentPage: number;
  hasMore: boolean;
  pageCount: number;
  scrollTop: number;
  /** Product to scroll into view after back-from-PDP (matches PlpRestoreState.restoreDesignId). */
  restoreDesignId?: number;
}

export function getPlpSnapshotKey(pathname: string): string {
  return `${PLP_SNAPSHOT_KEY_PREFIX}${pathname}`;
}

export function savePlpSessionSnapshot(pathname: string, snapshot: PlpSessionSnapshot): void {
  plpLscacheSet(getPlpSnapshotKey(pathname), snapshot);
}

export function readPlpSessionSnapshot(pathname: string): PlpSessionSnapshot | null {
  const parsed = plpLscacheGet<PlpSessionSnapshot>(getPlpSnapshotKey(pathname));
  if (!parsed) return null;
  if (parsed.version !== 1) return null;
  if (!parsed.listingMeta || !Array.isArray(parsed.designs)) return null;
  if (typeof parsed.currentPage !== 'number' || typeof parsed.pageCount !== 'number') return null;
  if (typeof parsed.hasMore !== 'boolean' || typeof parsed.scrollTop !== 'number') return null;
  return parsed;
}

export function clearPlpSessionSnapshot(pathname: string): void {
  plpLscacheRemove(getPlpSnapshotKey(pathname));
}

/**
 * Mobile back-from-PDP: if session has matching restore + listing snapshot, hydrate without refetch.
 */
export function tryReadMobileListingHydration(restorePath: string): {
  restore: PlpRestoreState;
  snapshot: PlpSessionSnapshot;
} | null {
  const saved = readPlpRestore(restorePath);
  const snap = readPlpSessionSnapshot(restorePath);
  if (!saved || !snap) return null;
  if (saved.pageCount !== snap.pageCount) return null;
  if (snap.designs.length === 0) return null;
  if (snap.currentPage !== saved.pageCount) return null;
  if (
    saved.restoreDesignId != null &&
    snap.restoreDesignId != null &&
    saved.restoreDesignId !== snap.restoreDesignId
  ) {
    return null;
  }
  return { restore: saved, snapshot: snap };
}
